import * as $ from 'jquery';
import { CLASS_CUSTOM_SIZE_HIDDEN, CLASS_HIDDEN_BY_WL } from '../ssb_constants';
import { convertUnit, isShowMeasurementsInDecimal } from './dimension';
const QADomain = (<any>window).ssb.QA_domain;

/**
 * Determines if the given modelType is a lean_to and the current domain is either
 * UrBanShed or The Original Mini Barns or is a QA domain.
 *
 * @param {string} modelType - The model type to check.
 * @returns {boolean} True if the modelType is lean_to and the domain matches UrBanShed
 *                    or The Original Mini Barns or is a QA domain, false otherwise.
 */
export const isRotateLoft = (modelType) => {
	if ('lean_to' !== modelType) {
		return false;
	}

	return isUrBanShed() || isTheOriginalMiniBarns() || isQADomain();
};

/**
 * Determines if the current domain is UrBanShed.
 *
 * @returns {boolean} True if the current domain matches UrBanShed, false otherwise.
 */
export const isUrBanShed = () => {
	const allowDomain = 'urbansheds.shedpro.co';

	return -1 !== window.location.hostname.indexOf(allowDomain) || allowDomain === QADomain;
};

/**
 * Determines if the current domain is Meridian Builds Inc.
 *
 * @returns {boolean} True if the current domain matches Meridian Builds Inc, false otherwise.
 */

export const isMeridianBuildsInc = () => {
	const allowDomain = 'meridianbuildsinc.shedpro.co';

	return -1 !== window.location.hostname.indexOf(allowDomain) || allowDomain === QADomain;
};

/**
 * Determines if the current domain is Robin Builders.
 *
 * @returns {boolean} True if the current domain matches Robin Builders, false otherwise.
 */

export const isRobinBuilders = () => {
	const allowDomain = 'robinbuilders.shedpro.co';

	return -1 !== window.location.hostname.indexOf(allowDomain) || allowDomain === QADomain;
};

/**
 * Determines if the current domain is The Original Mini Barns.
 *
 * @returns {boolean} True if the current domain matches The Original Mini Barns, false otherwise.
 */

export const isTheOriginalMiniBarns = () => {
	return -1 !== window.location.hostname.indexOf('theoriginalminibarns.shedpro.co');
};

/**
 * Determines if the current domain is one of the QA domains.
 *
 * @returns {boolean} True if the current domain is one of the QA domains, false otherwise.
 */
export const isQADomain = () => {
	const listDomain: any = ['theoriginalminibarns.shedpro.co', 'urbansheds.shedpro.co'];

	return listDomain.includes(QADomain);
};

/**
 * Determines if the current domain is Gold Star Shed.
 *
 * @returns {boolean} True if the current domain matches Gold Star Shed, false otherwise.
 */

export const isGoldStarShed = () => {
	const allowDomain = 'goldstarbuildings.shedpro.co';

	return -1 !== window.location.hostname.indexOf(allowDomain) || allowDomain === QADomain;
};

/**
 * Check if the current domain is a Texas Custom Shed domain
 * @returns {boolean} True if the current domain is a Texas Custom Shed domain, false otherwise
 */
export const isTexasCustomShed = () => {
	return -1 !== window.location.hostname.indexOf('texascustomsheds.shedpro.co');
};

/**
 * Check if the current domain is a Back Yard Office Utah domain
 * @returns {boolean} True if the current domain is a Back Yard Office Utah domain, false otherwise
 */
export const isBackYardOfficeUtah = () => {
	const url = new URL(window.location.href);
	const dev = url.searchParams.get('dev');

	return dev !== null || -1 !== window.location.hostname.indexOf('design.backyardofficeutah.com');
};

/**
 * Check if the current domain is the demo site
 * @returns {boolean} True if the current domain is the demo site, false otherwise
 */
export const isDemoSite = () => {
	const allowDomain = 'demo.shedpro.co';

	return -1 !== window.location.hostname.indexOf(allowDomain) || allowDomain === QADomain;
};

/**
 * Check if the current domain is the seadev site
 * @returns {boolean} True if the current domain is the seadev site, false otherwise
 */
export const isSeadevSite = () => {
	const allowDomain = 'seadev.shedpro.co';

	return -1 !== window.location.hostname.indexOf(allowDomain) || allowDomain === QADomain;
};

/**
 * Check if the current domain is the shed world site
 * @returns {boolean} True if the current domain is the shed world site, false otherwise
 */
export const isShedWorld = () => {
	const allowDomain = 'shedworld.shedpro.co';

	return -1 !== window.location.hostname.indexOf(allowDomain) || allowDomain === QADomain;
};

/**
 * Determines whether siding material price should be excluded based on the current domain.
 *
 * @returns {boolean} True if the current domain is either Urban Shed or Gold Star Shed, false otherwise.
 */

export const notIncludeSidingMaterialPrice = () => {
	return isUrBanShed() || isGoldStarShed();
};

/**
 * Checks if the current environment is configured as Smith Built.
 *
 * @returns {boolean} True if the environment is configured as Smith Built, false otherwise.
 */

export const isSmithBuilt = () => {
	return (<any>window).ssb.is_smithbuilt == '1';
};

/**
 * Retrieves the label for a given size component name.
 *
 * @param {jQuery} [element = $( `#ssb-configurator-size-area` ).children( 'option:selected' )] - The size component name.
 * @param {number} [height = Number( $( `#ssb-configurator-size-height` ).children( 'option:selected' ).data( 'size-height' ) )] - The height component name.
 * @returns {{label: string, widthDepthLabel: string, heightLabel: string, isCustomLabel: boolean}}
 *          An object containing the following:
 *          - label: The label for the given size component name.
 *          - widthDepthLabel: The label for the width and depth of the given size component name.
 *          - heightLabel: The label for the height of the given size component name.
 *          - isCustomLabel: True if the label is a custom label, false otherwise.
 */
export const getSizeLabel = (
	element = $(`#ssb-configurator-size-area`).children('option:selected'),
	height = Number(
		$(`#ssb-configurator-size-height`).children('option:selected').data('size-height'),
	),
) => {
	const customWidthInput = $('#ssb-custom-width');
	if (
		customWidthInput.length &&
		!$('#ssb-custom-size-fields-wrap').hasClass(CLASS_CUSTOM_SIZE_HIDDEN)
	) {
		const customWidth = Number($('#ssb-custom-width').val()) || 0;
		const customDepth = Number($('#ssb-custom-length').val()) || 0;
		const customHeight = Number($('#ssb-custom-height').val()) || 0;

		const customWidthLabel = String(convertUnit(customWidth)).trim();
		const customDepthLabel = String(convertUnit(customDepth)).trim();
		const customHeightLabel = String(convertUnit(customHeight)).trim();

		const label = `${customWidthLabel} x ${customDepthLabel} x ${customHeightLabel}`;
		const labelOld = `${customWidth} x ${customDepth} x ${customHeight}`;
		const widthDepthLabel = `${customWidthLabel} x ${customDepthLabel}`;
		const originWidthDepthLabel = `${customWidth} x ${customDepth}`;
		const heightLabel = `${customHeightLabel}`;

		return {
			label,
			widthDepthLabel,
			heightLabel,
			isCustomLabel: false,
			originWidthDepthLabel,
			labelOld,
		};
	}

	let label = '';
	let widthDepthLabel = '';
	let heightLabel = '';
	let dataSizes = element.data('sizes');
	let isCustomLabel = false;
	let originWidthDepthLabel = '';
	let labelOld = '';

	if (dataSizes) {
		dataSizes = JSON.parse(atob(dataSizes));
		if (dataSizes.length) {
			for (const size of dataSizes) {
				const { height: sizeHeight, name } = size;
				if (Number(height) === Number(sizeHeight) && name) {
					label = name.trim();
					labelOld = name.trim();
					isCustomLabel = true;
					const nameArr = name.split('x');
					if (nameArr.length <= 2) {
						widthDepthLabel = name.trim();
						originWidthDepthLabel = name.trim();
					} else {
						widthDepthLabel = `${nameArr[0]}x${nameArr[1]}`.trim();
						originWidthDepthLabel = `${nameArr[0]}x${nameArr[1]}`.trim();
						heightLabel = nameArr[2].trim();
					}
					break;
				}
			}
		}
	}
	if (!label) {
		const width = Number(element.data('size-width'));
		const depth = Number(element.data('size-depth'));
		heightLabel = String(convertUnit(height)).trim();
		let widthLabel = String(convertUnit(width)).trim();
		let depthLabel = String(convertUnit(depth)).trim();
		const isHiddenByWL = jQuery('#ssb-configurator-height').hasClass(CLASS_HIDDEN_BY_WL);
		if ('none' === jQuery('#ssb-configurator-height').css('display') && !isHiddenByWL) {
			label = `${widthLabel} x ${depthLabel}`;
			labelOld = `${width} x ${depth}`;
		} else {
			label = `${widthLabel} x ${depthLabel} x ${heightLabel}`;
			labelOld = `${width} x ${depth} x ${height}`;
		}

		widthDepthLabel = `${widthLabel} x ${depthLabel}`;
		originWidthDepthLabel = `${width} x ${depth}`;
		// heightLabel = `${height}`;
	} else if (!heightLabel) {
		heightLabel = `${height}`;
	}

	return { label, widthDepthLabel, heightLabel, isCustomLabel, originWidthDepthLabel, labelOld };
};

export const getElementWLByHeight = (height) => {
	let result = null;
	jQuery('#ssb-configurator-size-area option').each((_, element) => {
		const el = jQuery(element);
		const dataSizes = el.data('sizes');
		if (dataSizes) {
			const sizes = JSON.parse(atob(dataSizes));
			if (sizes.length) {
				for (const size of sizes) {
					const { height: sizeHeight, name } = size;
					if (Number(height) === Number(sizeHeight)) {
						result = el;
						return false;
					}
				}
			}
		}
	});
	return result || false;
};
